// Builds a branded PNG share card for a streak. Content is streak-count + JotBay
// branding ONLY — no personal or work data. Pure canvas (no external images).
// Tiered art: base (<30), milestone (>=30), century (>=100).
const FLAME_PATH =
  "M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z";

function tierOf(n) {
  if (n >= 100) return "century";
  if (n >= 30) return "milestone";
  return "base";
}

function drawRays(ctx, cx, cy, count, r0, r1, color, width) {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.lineCap = "round";
  for (let i = 0; i < count; i++) {
    const a = (i / count) * Math.PI * 2;
    ctx.beginPath();
    ctx.moveTo(cx + Math.cos(a) * r0, cy + Math.sin(a) * r0);
    ctx.lineTo(cx + Math.cos(a) * r1, cy + Math.sin(a) * r1);
    ctx.stroke();
  }
  ctx.restore();
}

function drawStar(ctx, cx, cy, spikes, outer, inner, color) {
  let rot = (Math.PI / 2) * 3;
  const step = Math.PI / spikes;
  ctx.beginPath();
  ctx.moveTo(cx, cy - outer);
  for (let i = 0; i < spikes; i++) {
    ctx.lineTo(cx + Math.cos(rot) * outer, cy + Math.sin(rot) * outer); rot += step;
    ctx.lineTo(cx + Math.cos(rot) * inner, cy + Math.sin(rot) * inner); rot += step;
  }
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
}

export function buildStreakCard(n) {
  const W = 1200, H = 630, cx = W / 2;
  const tier = tierOf(n);
  const c = document.createElement("canvas");
  c.width = W; c.height = H;
  const ctx = c.getContext("2d");

  // Background
  ctx.fillStyle = "#202123";
  ctx.fillRect(0, 0, W, H);
  const glowColor = tier === "century" ? "rgba(245,158,11,0.42)" : "rgba(15,157,138,0.40)";
  const glow = ctx.createRadialGradient(cx, 300, 40, cx, 300, 680);
  glow.addColorStop(0, glowColor);
  glow.addColorStop(1, "rgba(15,157,138,0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);

  const accent = tier === "base" ? "#F59E0B" : "#FBBF24"; // gold for milestone/century

  // Decorative rays behind the number (milestone + century)
  if (tier !== "base") {
    const rayCount = tier === "century" ? 40 : 28;
    drawRays(ctx, cx, 300, rayCount, 150, tier === "century" ? 300 : 250,
      tier === "century" ? "rgba(251,191,36,0.20)" : "rgba(15,157,138,0.22)", tier === "century" ? 4 : 3);
  }
  // Century: gold ring + scattered stars
  if (tier === "century") {
    ctx.save();
    ctx.strokeStyle = "rgba(251,191,36,0.55)";
    ctx.lineWidth = 6;
    ctx.beginPath(); ctx.arc(cx, 300, 210, 0, Math.PI * 2); ctx.stroke();
    ctx.restore();
    drawStar(ctx, cx - 300, 150, 5, 22, 9, "rgba(251,191,36,0.9)");
    drawStar(ctx, cx + 300, 160, 5, 18, 7, "rgba(251,191,36,0.8)");
    drawStar(ctx, cx + 330, 430, 5, 16, 6, "rgba(251,191,36,0.7)");
    drawStar(ctx, cx - 330, 440, 5, 20, 8, "rgba(251,191,36,0.8)");
  }

  // Brand wordmark (matches jotbay-logo.svg — teal pill, JOT white + BAY dark)
  ctx.fillStyle = "#202123";
  ctx.beginPath(); ctx.roundRect(80, 60, 188, 66, 33); ctx.fill();
  ctx.textBaseline = "middle";
  ctx.font = "800 42px Arial, Helvetica, sans-serif";
  {
    const jotW = ctx.measureText("JOT").width;
    const bayW = ctx.measureText("BAY").width;
    const startX = 80 + (188 - (jotW + bayW)) / 2;
    ctx.textAlign = "left";
    ctx.fillStyle = "#ffffff"; ctx.fillText("JOT", startX, 94);
    ctx.fillStyle = "#ffffff"; ctx.fillText("BAY", startX + jotW, 94);
  }

  // Tier label (uppercase, gold) above the flame
  if (tier !== "base") {
    ctx.textAlign = "center";
    ctx.fillStyle = "#FBBF24";
    ctx.font = "700 30px Georgia, 'Times New Roman', serif";
    ctx.fillText(tier === "century" ? "· CENTURY ·" : "· MILESTONE ·", cx, 118);
  }

  // Flame (filled), centered above the number
  ctx.save();
  const s = 5.6;
  ctx.translate(cx - 12 * s, 150);
  ctx.scale(s, s);
  ctx.fillStyle = accent;
  ctx.fill(new Path2D(FLAME_PATH));
  ctx.restore();

  // Big streak number
  ctx.textAlign = "center";
  ctx.fillStyle = accent;
  ctx.font = "800 210px Georgia, 'Times New Roman', serif";
  ctx.fillText(String(n), cx, 400);

  // "day streak"
  ctx.fillStyle = "#ffffff";
  ctx.font = "700 62px Georgia, 'Times New Roman', serif";
  ctx.fillText("day streak", cx, 508);

  // Tagline
  ctx.fillStyle = "rgba(255,255,255,0.6)";
  ctx.font = "400 30px Georgia, 'Times New Roman', serif";
  ctx.fillText("Jot it down. Report ready. Nothing forgotten. — JotBay", cx, 572);

  return new Promise((resolve) => c.toBlob(resolve, "image/png"));
}


// Weekly recap share card — STATS + branding only. No activity text, notes,
// objective names, or personal data (option a).
export function buildRecapCard(r) {
  const W = 1200, H = 630, cx = W / 2;
  const c = document.createElement("canvas");
  c.width = W; c.height = H;
  const ctx = c.getContext("2d");

  ctx.fillStyle = "#202123";
  ctx.fillRect(0, 0, W, H);
  const glow = ctx.createRadialGradient(cx, 300, 40, cx, 300, 700);
  glow.addColorStop(0, "rgba(15,157,138,0.38)");
  glow.addColorStop(1, "rgba(15,157,138,0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);

  // Brand wordmark (matches jotbay-logo.svg — teal pill, JOT white + BAY dark)
  ctx.fillStyle = "#202123";
  ctx.beginPath(); ctx.roundRect(80, 60, 188, 66, 33); ctx.fill();
  ctx.textBaseline = "middle";
  ctx.font = "800 42px Arial, Helvetica, sans-serif";
  {
    const jotW = ctx.measureText("JOT").width;
    const bayW = ctx.measureText("BAY").width;
    const startX = 80 + (188 - (jotW + bayW)) / 2;
    ctx.textAlign = "left";
    ctx.fillStyle = "#ffffff"; ctx.fillText("JOT", startX, 94);
    ctx.fillStyle = "#ffffff"; ctx.fillText("BAY", startX + jotW, 94);
  }

  // Header label
  ctx.textAlign = "center";
  ctx.fillStyle = "#FBBF24";
  ctx.font = "700 34px Georgia, 'Times New Roman', serif";
  ctx.fillText("MY WEEK", cx, 190);

  // Four stats in a row
  const stats = [
    [`${r.total_hours}h`, "logged"],
    [`${r.entry_count}`, "activities"],
    [`${r.active_days}/7`, "days active"],
    [`${r.streak}`, "day streak"],
  ];
  const xs = [W * 0.17, W * 0.39, W * 0.61, W * 0.83];
  stats.forEach(([val, label], i) => {
    ctx.fillStyle = i === 3 ? "#F59E0B" : "#ffffff";
    ctx.font = "800 92px Georgia, 'Times New Roman', serif";
    ctx.fillText(val, xs[i], 350);
    ctx.fillStyle = "rgba(255,255,255,0.65)";
    ctx.font = "400 30px Georgia, 'Times New Roman', serif";
    ctx.fillText(label, xs[i], 430);
  });

  // Divider + tagline
  ctx.strokeStyle = "rgba(255,255,255,0.12)";
  ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(120, 500); ctx.lineTo(W - 120, 500); ctx.stroke();
  ctx.fillStyle = "rgba(255,255,255,0.6)";
  ctx.font = "400 30px Georgia, 'Times New Roman', serif";
  ctx.fillText("Jot it down. Report ready. Nothing forgotten. — JotBay", cx, 555);

  return new Promise((resolve) => c.toBlob(resolve, "image/png"));
}
